import {
  DEFAULT_EARLY_EVALUATION_GUIDANCE,
  EARLY_QUALITY_MEDIA_WAIT_MS,
  EARLY_QUALITY_DEADLINE_MS,
  EARLY_QUALITY_MIN_REMAINING_OPEN_ENDED,
  EARLY_QUALITY_MAX_INTERVIEW_DURATION_MS,
} from "./constants";
import type {
  InterviewBlock,
  InterviewItemTemplateId,
  QAState,
} from "@/components/interview/types";
import { qualityScoreReasonEnum } from "@/components/responses/evaluation/quality/schema";
import { zodBrandedUuid, isNullish } from "@/utils/helpers";
import { interviewTemplatesById } from "@/utils/questions";
import type { OrganizationId } from "@/utils/types";
import { z } from "zod";

export type OpenEndedTurn = Pick<
  QAState,
  "submittedAt" | "currentTemplateId" | "rawInput" | "input" | "voiceInput"
>;
type SubmittedOpenEndedTurn = OpenEndedTurn & { submittedAt: string };
type OpenEndedTemplateChecker = (
  templateId: InterviewItemTemplateId
) => boolean;

export const openEndedTemplateChecker = (
  blocks: InterviewBlock[]
): OpenEndedTemplateChecker => {
  const templates = interviewTemplatesById(
    blocks.filter((block) => block.type !== "screening")
  );
  return (templateId) => {
    const template = templates.get(templateId);
    return (
      template?.type === "question" && template.questionType === "open_ended"
    );
  };
};

const isSubmittedOpenEnded = (
  turn: OpenEndedTurn,
  isOpenEndedTemplate: OpenEndedTemplateChecker
): turn is SubmittedOpenEndedTurn =>
  Boolean(turn.submittedAt) &&
  isOpenEndedTemplate(turn.currentTemplateId) &&
  (turn.rawInput || turn.input || turn.voiceInput || "").trim().length > 0;

export const findOpenEndedPrefixEnd = ({
  history,
  count,
  isOpenEndedTemplate,
}: {
  history: OpenEndedTurn[];
  count: number;
  isOpenEndedTemplate: OpenEndedTemplateChecker;
}): { endIndex: number; triggeredAt: string } | null => {
  const prefixEnds = history.flatMap((turn, index) =>
    isSubmittedOpenEnded(turn, isOpenEndedTemplate)
      ? [{ endIndex: index + 1, triggeredAt: turn.submittedAt }]
      : []
  );
  return prefixEnds[count - 1] ?? null;
};

export const isEarlyQualityCheckExpired = ({
  triggeredAt,
  now = Date.now(),
  deadlineMs = EARLY_QUALITY_DEADLINE_MS,
}: {
  triggeredAt: string;
  now?: number;
  deadlineMs?: number;
}): boolean => {
  const triggeredAtMs = Date.parse(triggeredAt);
  return !Number.isFinite(triggeredAtMs) || now >= triggeredAtMs + deadlineMs;
};

export const countOpenEndedJustReached = ({
  history,
  isOpenEndedTemplate,
}: {
  history: OpenEndedTurn[];
  isOpenEndedTemplate: OpenEndedTemplateChecker;
}): number | null => {
  const lastSubmitted = history.findLast((turn) => Boolean(turn.submittedAt));
  if (
    !lastSubmitted ||
    !isSubmittedOpenEnded(lastSubmitted, isOpenEndedTemplate)
  ) {
    return null;
  }
  return history.filter((turn) =>
    isSubmittedOpenEnded(turn, isOpenEndedTemplate)
  ).length;
};

const earlyQualityTriggerSchema = z
  .object({
    criteria: z.literal("open_ended"),
    value: z.number().int().positive(),
    dry_run: z.boolean().default(true),
    min_confidence: z.number().min(0).max(1).default(0),
    deadline_ms: z.number().int().positive().default(EARLY_QUALITY_DEADLINE_MS),
    min_remaining_open_ended: z
      .number()
      .int()
      .nonnegative()
      .default(EARLY_QUALITY_MIN_REMAINING_OPEN_ENDED),
    max_interview_duration_ms: z
      .number()
      .int()
      .positive()
      .default(EARLY_QUALITY_MAX_INTERVIEW_DURATION_MS),
    skipped_reasons: z.array(qualityScoreReasonEnum).default([]),
    early_evaluation_guidance: z
      .string()
      .trim()
      .default(DEFAULT_EARLY_EVALUATION_GUIDANCE)
      .transform((guidance) => guidance || DEFAULT_EARLY_EVALUATION_GUIDANCE),
    media_wait_ms: z
      .number()
      .int()
      .nonnegative()
      .default(EARLY_QUALITY_MEDIA_WAIT_MS)
      .transform((wait) => Math.min(wait, EARLY_QUALITY_MEDIA_WAIT_MS)),
  })
  .strict();

export const earlyQualityPayloadSchema = z
  .object({
    panels: z
      .array(z.enum(["prolific", "user_interviews", "respondent_b2c"]))
      .optional(),
    sources: z.array(z.string().min(1)).optional(),
    orgs: z.array(zodBrandedUuid<OrganizationId>()).optional(),
    excludedOrgs: z.array(zodBrandedUuid<OrganizationId>()).optional(),
    triggers: z.array(earlyQualityTriggerSchema).default([]),
  })
  .strict()
  .refine(
    ({ triggers }) =>
      new Set(triggers.map((trigger) => trigger.value)).size ===
      triggers.length,
    { message: "Each open_ended trigger must have a unique value" }
  );

export type EarlyQualityFlagConfig = z.infer<typeof earlyQualityPayloadSchema>;

export const parseEarlyQualityPayload = (
  payload: unknown
): EarlyQualityFlagConfig => {
  if (isNullish(payload)) return { triggers: [] };
  const result = earlyQualityPayloadSchema.safeParse(payload);
  if (!result.success) {
    console.error("[EARLY_QUALITY] Invalid flag payload", {
      issues: result.error.issues,
    });
    return { triggers: [] };
  }
  return result.data;
};
